import { type ReactNode } from 'react';

import { NavLink } from 'react-router-dom';

import { useGame } from '@/hooks/useGame';
import { gateFor, type DashboardItem } from '@/models/dashboardItem';
import { type PermissionCode } from '@/models/permission';

import { GameImage } from '../gameMiniature';
import { PermissionCheck } from '../permissionCheck';
import { GameSwitcher } from './gameSwitcher';
import {
  Sidebar as ShadSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '../ui/sidebar';

interface ProSidebarProps {
  items?: DashboardItem[];
}

// Scoped to the current route, so the same entry can be hidden on one Sandbox and
// shown on another.
const NavGate = ({
  permissions,
  children,
}: {
  permissions?: PermissionCode[];
  children: ReactNode;
}) =>
  permissions ? (
    <PermissionCheck oneOf={permissions}>{children}</PermissionCheck>
  ) : (
    children
  );

const MenuItemWithChildren = ({ item }: { item: DashboardItem }) => {
  return (
    <NavGate permissions={gateFor(item)}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild>
          <NavLink end to={item.link}>
            {item.icon} {item.label}
          </NavLink>
        </SidebarMenuButton>
        {item.items && item.items.length > 0 && (
          <SidebarMenuSub>
            {item.items.map((_item) => (
              <NavGate key={_item.label} permissions={_item.permissions}>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild>
                    <NavLink
                      end
                      to={`${item.link}/${_item.link}`}
                      className={({ isActive }) => (isActive ? 'active' : '')}
                    >
                      <span>{_item.icon}</span>
                      <span>{_item.label}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </NavGate>
            ))}
          </SidebarMenuSub>
        )}
      </SidebarMenuItem>
    </NavGate>
  );
};

export const AppSidebar = ({ items = [] }: ProSidebarProps) => {
  const game = useGame();
  return (
    <ShadSidebar>
      <SidebarHeader>
        <GameSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item) => (
              <MenuItemWithChildren key={item.label} item={item} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="aspect-square size-36 mx-auto grid place-items-center ">
              <GameImage game={game} />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </ShadSidebar>
  );
};
